import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { colors } from '../../theme';
import Text from './Text';

/**
 * "Search for <term>", where the term cycles.
 *
 * Shared by the button on Home and the real field on the search screen, which
 * need the identical hint for the identical reason - the field advertises what
 * can be found rather than describing itself. Two copies of a timer like this
 * drift, and the two surfaces would end up cycling different words at
 * different speeds.
 *
 * Presentation only. Nothing here reads or writes the query; on the search
 * screen the caller hides it the moment anything is typed.
 */

const HOLD_MS = 1800;
const FADE_MS = 260;
/** How far the outgoing term rises and the incoming one starts below. */
const TRAVEL = 12;

type Props = {
  hints: string[];
  /** The fixed half, before the cycling term. */
  prefix?: string;
  style?: StyleProp<ViewStyle>;
};

function RotatingHint({ hints, prefix = 'Search for ', style }: Props) {
  const [slot, setSlot] = useState(0);
  const swap = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    if (hints.length < 2) {
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const cycle = () => {
      timer = setTimeout(() => {
        if (cancelled) {
          return;
        }
        // Fade the current term out, swap the text while nothing is visible,
        // then fade the next one in from below.
        Animated.timing(swap, {
          toValue: 0,
          duration: FADE_MS,
          easing: Easing.in(Easing.quad),
          useNativeDriver: true,
        }).start(({ finished }) => {
          if (!finished || cancelled) {
            return;
          }
          setSlot(current => (current + 1) % hints.length);
          Animated.timing(swap, {
            toValue: 1,
            duration: FADE_MS,
            easing: Easing.out(Easing.quad),
            useNativeDriver: true,
          }).start(() => {
            if (!cancelled) {
              cycle();
            }
          });
        });
      }, HOLD_MS);
    };

    cycle();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [hints, swap]);

  return (
    <View style={[styles.hint, style]}>
      <Text variant="body" color={colors.inkSubtle}>
        {prefix}
      </Text>
      <Animated.View
        style={{
          opacity: swap,
          transform: [
            {
              translateY: swap.interpolate({
                inputRange: [0, 1],
                outputRange: [TRAVEL, 0],
              }),
            },
          ],
        }}
      >
        <Text variant="body" color={colors.inkBody} numberOfLines={1}>
          {hints[slot]}
        </Text>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  /* Clips the incoming term while it travels up into place. */
  hint: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    overflow: 'hidden',
  },
});

export default RotatingHint;
